import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { crmApi, customerOrderFocusPath, extractCrmError } from "@/lib/crmApi";
import { getDefaultPromisedDeliveryDate } from "@/components/customer-orders/CustomerOrderFormDialog";
import ClientSearch from "@/components/customer-orders/ClientSearch";
import {
  calculateDraftTotals,
  calculateLineTotal,
  clearDraftItems,
  draftItemsToApiPayload,
  formatDraftMoney,
  removeDraftItem,
  setDraftClient,
  setDraftOrderDiscount,
  updateDraftItem,
} from "@/lib/customerOrderDraft";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";

export default function CustomerOrderDraftPanel({
  token,
  draft,
  onDraftChange,
  onDraftReset,
  disabled,
}) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const totals = calculateDraftTotals(draft.items, draft.discount_percent);

  const handleClientSelect = (client) => {
    onDraftChange(setDraftClient(draft, client));
  };

  const handleItemPatch = (key, patch) => {
    onDraftChange(updateDraftItem(draft, key, patch));
  };

  const handleRemoveItem = (key) => {
    onDraftChange(removeDraftItem(draft, key));
  };

  const handleClear = () => {
    if (!draft.items.length) return;
    if (!window.confirm("Очистить заказ клиента?")) return;
    onDraftChange(clearDraftItems(draft));
    toast.success("Заказ клиента очищен");
  };

  const handleCreateOrder = async () => {
    if (creating || disabled) return;
    if (!draft.client?.id) {
      toast.error("Выберите клиента");
      return;
    }
    if (!draft.items.length) {
      toast.error("Добавьте позиции в заказ");
      return;
    }

    const orderDiscountPercent = Number(draft.discount_percent) || 0;
    if (orderDiscountPercent < 0 || orderDiscountPercent > 100) {
      toast.error("Скидка на заказ должна быть от 0 до 100%");
      return;
    }

    for (const item of draft.items) {
      const quantity = Number(item.quantity);
      const price = Number(item.price);
      const discountPercent = Number(item.discount_percent);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        toast.error("Проверьте количество позиций");
        return;
      }
      if (!Number.isFinite(price) || price < 0) {
        toast.error("Проверьте цены позиций");
        return;
      }
      if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
        toast.error("Скидка по позициям должна быть от 0 до 100%");
        return;
      }
    }

    setCreating(true);
    try {
      const payload = {
        client_id: draft.client.id,
        source: "catalog",
        status: "draft",
        promised_delivery_date: getDefaultPromisedDeliveryDate(),
        discount_percent: orderDiscountPercent > 0 ? orderDiscountPercent : null,
        items: draftItemsToApiPayload(draft.items),
      };
      const created = await crmApi.createCustomerOrder(token, payload);
      onDraftReset();
      toast.success("Заказ покупателя создан");
      navigate(customerOrderFocusPath(created.id));
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось создать заказ"));
    } finally {
      setCreating(false);
    }
  };

  if (!draft.items.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-4 text-center text-sm text-gray-500">
        <p>Добавьте товары из прайса кнопкой «Добавить в заказ клиента».</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="max-w-md">
          <ClientSearch
            token={token}
            selectedClient={draft.client}
            onSelect={handleClientSelect}
            disabled={disabled || creating}
            variant="catalog"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-100">
            <tr>
              <th className="px-2 py-2 text-left font-semibold text-gray-700">Артикул</th>
              <th className="px-2 py-2 text-left font-semibold text-gray-700">Наименование</th>
              <th className="px-2 py-2 text-center font-semibold text-gray-700">Кол-во</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-700">Цена</th>
              <th className="px-2 py-2 text-center font-semibold text-gray-700">Скидка %</th>
              <th className="px-2 py-2 text-left font-semibold text-gray-700">Комментарий</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-700">Сумма</th>
              <th className="w-12 px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {draft.items.map((item) => (
              <tr key={item.__key} className="hover:bg-green-50/30">
                <td className="px-2 py-2 font-mono text-xs text-gray-800">
                  {item.oem || item.article || "—"}
                </td>
                <td className="px-2 py-2 text-gray-900">
                  <div>{item.name || "—"}</div>
                  {item.supplier && (
                    <div className="text-xs text-gray-500">{item.supplier}</div>
                  )}
                </td>
                <td className="px-2 py-2 text-center">
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemPatch(item.__key, {
                        quantity: parseInt(e.target.value, 10) || 1,
                      })
                    }
                    className="mx-auto h-7 w-16 text-center text-sm"
                    disabled={disabled || creating}
                  />
                </td>
                <td className="px-2 py-2 text-right">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={item.price}
                    onChange={(e) =>
                      handleItemPatch(item.__key, {
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="ml-auto h-7 w-24 text-right text-sm"
                    disabled={disabled || creating}
                  />
                </td>
                <td className="px-2 py-2 text-center">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={item.discount_percent}
                    onChange={(e) =>
                      handleItemPatch(item.__key, {
                        discount_percent: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="mx-auto h-7 w-16 text-center text-sm"
                    disabled={disabled || creating}
                  />
                </td>
                <td className="px-2 py-2">
                  <Input
                    value={item.comment || ""}
                    onChange={(e) =>
                      handleItemPatch(item.__key, {
                        comment: e.target.value,
                      })
                    }
                    placeholder="Комментарий"
                    className="h-7 min-w-[120px] text-sm"
                    disabled={disabled || creating}
                  />
                </td>
                <td className="px-2 py-2 text-right font-semibold text-gray-900">
                  {formatDraftMoney(calculateLineTotal(item))}
                </td>
                <td className="px-2 py-2 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveItem(item.__key)}
                    className="h-7 w-7 p-0 hover:bg-red-50"
                    disabled={disabled || creating}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="flex-shrink-0 border-t-2 border-gray-300 bg-gradient-to-r from-gray-50 to-green-50 px-4 py-3"
        style={{ minHeight: "96px" }}
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4 text-sm">
            <div>
              <span className="text-gray-600">Без скидки:</span>
              <span className="ml-1 font-semibold text-gray-900">
                {formatDraftMoney(totals.subtotal)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Скидка по позициям:</span>
              <span className="ml-1 font-semibold text-amber-700">
                −{formatDraftMoney(totals.lineDiscount)}
              </span>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Скидка на заказ, %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={draft.discount_percent ?? 0}
                onChange={(e) =>
                  onDraftChange(
                    setDraftOrderDiscount(draft, parseFloat(e.target.value) || 0)
                  )
                }
                className="mt-1 h-8 w-20 text-center text-sm"
                disabled={disabled || creating}
              />
            </div>
            {totals.orderDiscount > 0 && (
              <div>
                <span className="text-gray-600">Скидка на заказ:</span>
                <span className="ml-1 font-semibold text-amber-700">
                  −{formatDraftMoney(totals.orderDiscount)}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-600">К оплате:</span>
              <span className="ml-1 text-lg font-bold text-green-700">
                {formatDraftMoney(totals.total)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleClear}
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
              disabled={disabled || creating}
            >
              Очистить заказ
            </Button>
            <Button
              onClick={handleCreateOrder}
              disabled={disabled || creating || !draft.client?.id}
              className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 font-semibold text-white shadow-lg hover:from-green-700 hover:to-emerald-700"
            >
              {creating ? "Создание..." : "Создать заказ покупателя"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
